import type { JSX } from 'react';

import { Text } from '@change/ds-core-ui/next';

import { Translate, TranslatePlural, TranslatePluralHtml } from '@change-corgi/core/react/i18n';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconArrowUpward, iconShare, iconVisibility } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { InfoTooltip } from 'src/app/shared/components/tooltips';

import { useDashboardReskinExperiment } from '../../../../../../hooks/useDashboardReskinExperiment';

import { Speedometer } from './components/Speedometer';
import { usePetitionStats } from './hooks/usePetitionStats';
import { PetitionStats as PetitionStatsReskin } from './index_new';

export function PetitionStats(): JSX.Element {
	const { data } = usePetitionStats();
	const {
		data: { isEnabled },
	} = useDashboardReskinExperiment();

	if (isEnabled) {
		return <PetitionStatsReskin />;
	}

	return (
		<Flex sx={{ gap: '16px', flexDirection: 'column' }} data-testid="petition-stats-control">
			<div sx={{ display: ['flex', 'none'], justifyContent: 'space-between', alignItems: 'center' }}>
				<Heading
					as="h2"
					size={['h4', 'h3']}
					id="petition-overview-title"
					data-qa="petition-overview-title"
					data-testid="petition-overview-title"
				>
					<Translate value="corgi.petition.starter_dashboard.petition_overview.title" />
				</Heading>
				<InfoTooltip>
					<Translate value="corgi.petition.starter_dashboard.petition_overview.tip" />
				</InfoTooltip>
			</div>
			<Card
				p="24px"
				backgroundColor="white"
				sx={{
					border: 'unset',
					display: 'grid',
					gap: '16px',
					gridTemplateColumns: ['1fr', '1fr 1fr'],
					width: '100%',
				}}
			>
				<Box data-qa="circular-progress-bar" sx={{ display: 'grid', position: 'relative' }}>
					<Speedometer />
					<Text
						sx={{
							textAlign: 'center',
							width: ['25ch', '100%'],
							position: 'absolute',
							bottom: [0, '-20%', 0],
							left: '50%',
							transform: 'translateX(-50%)',
						}}
					>
						<TranslatePluralHtml
							value="corgi.petition.starter_dashboard.petition_overview.progress.num_to_goal"
							count={data.signaturesToGoal}
							replacements={{
								number: data.signaturesToGoalFormatted,
							}}
						/>
					</Text>
				</Box>

				<Flex sx={{ justifyContent: 'center', alignItems: 'center', width: '100%' }}>
					<Flex sx={{ flexDirection: 'column', gap: [16, 24] }}>
						<Text data-testid="view-count" sx={{ display: 'flex', alignItems: 'center' }}>
							<Icon icon={iconVisibility} color="purple" mr="10px" size="xl" />
							<TranslatePlural
								value="corgi.petition.starter_dashboard.petition_overview.stats.views"
								count={data.views}
								replacements={{ viewCount: data.viewsFormatted }}
							/>
						</Text>
						<Text data-testid="share-count" sx={{ display: 'flex', alignItems: 'center' }}>
							<Icon icon={iconShare} color="purple" mr="10px" size="xl" />
							<TranslatePlural
								value="corgi.petition.starter_dashboard.petition_overview.stats.shares"
								count={data.shares}
								replacements={{ shareCount: data.sharesFormatted }}
							/>
						</Text>
						{data.shouldShowPromotions && (
							<Text data-testid="promote-count" sx={{ display: 'flex', alignItems: 'center' }}>
								<Icon icon={iconArrowUpward} color="purple" mr="10px" size="xl" />
								<TranslatePlural
									value="corgi.petition.starter_dashboard.petition_overview.stats.promoters"
									count={data.promoters}
									replacements={{ promoterCount: data.promotersFormatted }}
								/>
							</Text>
						)}
						{/* TODO: hookup comments */}
						{/* <Text data-testid="comment-count" sx={{ display: 'flex', alignItems: 'center' }}>
						<Icon icon={iconComment} color="purple" mr="10px" size="xl" />
						<TranslatePlural
							value="corgi.petition.starter_dashboard.petition_overview.stats.comments"
							count={data.comments}
							replacements={{ commentCount: data.commentsFormatted }}
						/>
					</Text> */}
					</Flex>
				</Flex>
			</Card>
		</Flex>
	);
}
